import React from 'react';
import { User } from 'lucide-react';
import {
  Input,
  Label,
  Card,
  CardContent,
  CardHeader,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  DatePicker,
} from '@roger/ui-components';
import type {
  EmploymentStatus,
  Gender,
  MaritalStatus,
} from '../../services/api/types.gen';
import { formatSSN } from '../../utils/intake';
import {
  EMPLOYMENT_STATUS_OPTIONS,
  GENDER_OPTIONS,
  MARITAL_STATUS_OPTIONS,
} from '../../constants/options';
import {
  isValidMrn,
  MAX_LENGTHS,
  MRN_ERROR_MESSAGE,
  NONE_VALUE,
  type PatientInfoSectionProps,
} from './patientInfoForm';

// Name, DOB, gender, identifiers, language, marital and employment status.
// Controlled by PatientInfoTab, which owns the form state and autosave.
export const PatientDemographicSection: React.FC<PatientInfoSectionProps> = ({
  form,
  updateField,
  canEditPatientInfo,
}) => {
  return (
    <Card>
      <CardHeader className='pb-4'>
        <div className='flex items-center gap-2'>
          <User className='h-4 w-4 text-muted-foreground' />
          <h3 className='text-sm font-semibold'>Demographic Information</h3>
        </div>
      </CardHeader>
      <CardContent>
        <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
          <div className='space-y-1.5'>
            <Label htmlFor='first_name'>
              First Name <span className='text-destructive'>*</span>
            </Label>
            <Input
              id='first_name'
              value={form.first_name}
              onChange={e => updateField('first_name', e.target.value)}
              disabled={!canEditPatientInfo}
              inputSize='sm'
            />
          </div>
          <div className='space-y-1.5'>
            <Label htmlFor='middle_name'>Middle Name</Label>
            <Input
              id='middle_name'
              value={form.middle_name}
              onChange={e => updateField('middle_name', e.target.value)}
              disabled={!canEditPatientInfo}
              inputSize='sm'
            />
          </div>
          <div className='space-y-1.5'>
            <Label htmlFor='last_name'>
              Last Name <span className='text-destructive'>*</span>
            </Label>
            <Input
              id='last_name'
              value={form.last_name}
              onChange={e => updateField('last_name', e.target.value)}
              disabled={!canEditPatientInfo}
              inputSize='sm'
            />
          </div>
          <div className='space-y-1.5'>
            <Label htmlFor='suffix'>Suffix</Label>
            <Input
              id='suffix'
              value={form.suffix}
              onChange={e => updateField('suffix', e.target.value)}
              disabled={!canEditPatientInfo}
              placeholder='Jr, Sr, III'
              maxLength={MAX_LENGTHS.suffix}
              inputSize='sm'
            />
          </div>
          <div className='space-y-1.5'>
            <Label>Date of Birth</Label>
            <DatePicker
              date={form.date_of_birth}
              onDateChange={d => updateField('date_of_birth', d)}
              disabled={!canEditPatientInfo}
              datePickerSize='sm'
              className='w-full'
            />
          </div>
          <div className='space-y-1.5'>
            <Label>Gender</Label>
            <Select
              value={form.gender}
              onValueChange={v => updateField('gender', v as Gender)}
            >
              <SelectTrigger selectSize='sm' disabled={!canEditPatientInfo}>
                <SelectValue placeholder='Select gender' />
              </SelectTrigger>
              <SelectContent>
                {GENDER_OPTIONS.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className='space-y-1.5'>
            <Label htmlFor='ssn'>SSN</Label>
            <Input
              id='ssn'
              value={form.ssn}
              onChange={e => updateField('ssn', formatSSN(e.target.value))}
              disabled={!canEditPatientInfo}
              placeholder='XXX-XX-XXXX'
              inputSize='sm'
            />
          </div>
          <div className='space-y-1.5'>
            <Label htmlFor='mbi'>MBI</Label>
            <Input
              id='mbi'
              value={form.mbi}
              onChange={e => updateField('mbi', e.target.value)}
              disabled={!canEditPatientInfo}
              placeholder='1EG4-TE5-MK72'
              inputSize='sm'
            />
          </div>
          <div className='space-y-1.5'>
            <Label htmlFor='mrn'>MRN</Label>
            <Input
              id='mrn'
              value={form.mrn}
              onChange={e => updateField('mrn', e.target.value)}
              disabled={!canEditPatientInfo}
              maxLength={MAX_LENGTHS.mrn}
              inputSize='sm'
              aria-invalid={!isValidMrn(form.mrn) || undefined}
              aria-describedby={!isValidMrn(form.mrn) ? 'mrn-error' : undefined}
            />
            {!isValidMrn(form.mrn) && (
              <p
                id='mrn-error'
                className='mt-1 text-xs text-destructive'
                role='alert'
              >
                {MRN_ERROR_MESSAGE}
              </p>
            )}
          </div>
          <div className='space-y-1.5'>
            <Label htmlFor='primary_language'>Primary Language</Label>
            <Input
              id='primary_language'
              value={form.primary_language}
              onChange={e => updateField('primary_language', e.target.value)}
              disabled={!canEditPatientInfo}
              inputSize='sm'
            />
          </div>
          <div className='space-y-1.5'>
            <Label>Marital Status</Label>
            <Select
              value={form.marital_status || NONE_VALUE}
              onValueChange={v =>
                updateField(
                  'marital_status',
                  v === NONE_VALUE ? '' : (v as MaritalStatus)
                )
              }
            >
              <SelectTrigger selectSize='sm' disabled={!canEditPatientInfo}>
                <SelectValue placeholder='Select marital status' />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE_VALUE}>Not specified</SelectItem>
                {MARITAL_STATUS_OPTIONS.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className='space-y-1.5'>
            <Label>Employment Status</Label>
            <Select
              value={form.employment_status || NONE_VALUE}
              onValueChange={v =>
                updateField(
                  'employment_status',
                  v === NONE_VALUE ? '' : (v as EmploymentStatus)
                )
              }
            >
              <SelectTrigger selectSize='sm' disabled={!canEditPatientInfo}>
                <SelectValue placeholder='Select employment status' />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE_VALUE}>Not specified</SelectItem>
                {EMPLOYMENT_STATUS_OPTIONS.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
